import { BaseIPCConnection } from "@nexusmods/fomod-installer-ipc";
import { statAsync } from "../../../util/fs";
import { log } from "../../../util/log";
import path from "path";
import getVortexPath from "../../../util/getVortexPath";
/**
 * Vortex-specific IPC connection implementation
 * Extends BaseIPCConnection with Vortex API integration:
 * - Vortex logging system
 * - Vortex file system (fs) utilities
 * - Vortex path resolution (getVortexPath)
 * - Vortex dialog system (showDialog/closeDialog)
 * - FOMOD-specific commands (testSupported, install)
 */
export class VortexIPCConnection extends BaseIPCConnection {
    /**
     * Create a new Vortex IPC connection with fallback strategies
     *
     * @param api Vortex API for showing dialogs and translations
     * @param strategies Array of transport-launcher pairs to try (in order)
     * @param connectionTimeout Timeout in milliseconds (default: 10000)
     * @param modName Optional mod name for dialog display purposes
     *
     * @example
     * const connection = new VortexIPCConnection(api, [
     *   { transport: namedPipeTransport, launcher: sandboxLauncher },
     *   { transport: tcpTransport, launcher: regularLauncher }
     * ], 10000);
     */
    constructor(api, strategies, connectionTimeout = 10000, modName) {
        // Create timeout options with Vortex dialog integration
        const timeoutOptions = {
            showDialog: !!api,
            onTimeoutDialog: api
                ? async (dialogId, command) => {
                    const t = api.translate;
                    const no = t("Cancel");
                    const yes = t("Continue Installation");
                    const dialogResult = await api.showDialog?.("question", t("Installation Timeout"), {
                        bbcode: t(`The installation of mod "{{ modName }}" is taking longer than expected.[br][/br][br][/br]` +
                            `This may happen because the mod has a custom dialog that Vortex is not aware of.[br][/br]` +
                            `In this case, you may need to interact with the mod's installer manually outside of Vortex to proceed.[br][/br][br][/br]` +
                            `Would you like to continue with the installation, or cancel it?`, { replace: { modName: modName } }),
                    }, [{ label: no }, { label: yes } /*{ label: yesForAll }*/], dialogId);
                    // Return true to continue, 'cancel' if user clicked Cancel, false for other cases
                    if (dialogResult?.action === yes) {
                        return true;
                    }
                    else if (dialogResult?.action === no) {
                        return "cancel";
                    }
                    return false; // Dialog dismissed or other cases
                }
                : undefined,
            onDismissDialog: api
                ? (dialogId) => {
                    api.closeDialog?.(dialogId);
                }
                : undefined,
        };
        super(strategies, connectionTimeout, timeoutOptions);
    }
    getExecutablePaths(exeName) {
        const paths = super.getExecutablePaths(exeName);
        paths.push(path.join(getVortexPath("package_unpacked"), "node_modules", "@nexusmods/fomod-installer-ipc", "dist", exeName));
        return paths;
    }
    /**
     * Implement logging using Vortex log system
     */
    log(level, message, metadata) {
        log(level, message, metadata);
    }
    /**
     * Implement file existence check using Vortex fs utilities
     */
    async fileExists(filePath) {
        try {
            const stat = await statAsync(filePath);
            return stat.isFile();
        }
        catch (err) {
            return false;
        }
    }
    /**
     * Send TestSupported command to check if files are supported by a FOMOD installer
     */
    async testSupported(files, allowedTypes) {
        const response = await this.sendCommand("TestSupported", {
            files,
            allowedTypes,
        });
        return {
            supported: response?.supported ?? false,
            requiredFiles: response?.requiredFiles ?? [],
        };
    }
    /**
     * Send Install command to perform FOMOD installation
     */
    async install(files, stopPatterns, pluginPath, scriptPath, fomodChoices, validate) {
        // Grant access to the mod installation directory if using sandbox
        // scriptPath is the destinationPath where mod files are being installed
        if (scriptPath) {
            await this.grantAdditionalAccess([scriptPath]);
        }
        const response = await this.sendCommand("Install", {
            files,
            stopPatterns,
            pluginPath,
            scriptPath,
            fomodChoices,
            validate,
        });
        return response;
    }
}
